import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, FileSearch, Loader2, X } from 'lucide-react';
import ActionButton from '../Buttons/ActionButton';
import SearchInputField from '../Forms/Input/SearchInputField';
import DatePicker from '../Forms/DatePicker/DatePicker';
import PurchaseReturnEventService from '../../services/fiscal/PurchaseReturnEventService';
import { PurchaseReturnDocumentCandidate } from '../../types/fiscal/PurchaseReturnEvent';

interface Props {
  selected?: PurchaseReturnDocumentCandidate[];
  onSelect: (candidate: PurchaseReturnDocumentCandidate) => void | Promise<void>;
  mode?: 'single' | 'multiple';
  disabled?: boolean;
  /**
   * Escopa la búsqueda y las reglas de compatibilidad a este proveedor/tipo
   * aunque `selected` no traiga un `candidate` con esos datos (p. ej. el
   * documento inicial cargado por `initialPurchaseId` solo trae
   * `eligibility`, no `candidate`).
   */
  supplierId?: number | null;
  documentType?: string;
  /** IDs de compra ya agregados al evento, aunque no tengan `candidate` (mismo caso que `supplierId`). */
  selectedPurchaseIds?: number[];
}

const isCompleteDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value);

const documentTypeLabel = (type: string) => ({ '14': 'Factura de sujeto excluido' } as Record<string, string>)[type] || type || 'Sin tipo';
const dateLabel = (date?: string) => date ? new Intl.DateTimeFormat('es-SV', { dateStyle: 'medium' }).format(new Date(date)) : '—';
const money = (value: number) => new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(Number(value || 0));

export default function PurchaseReturnDocumentSelector({ selected = [], onSelect, mode = 'multiple', disabled = false, supplierId: supplierIdProp, documentType: documentTypeProp, selectedPurchaseIds = [] }: Props) {
  const [query, setQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [documents, setDocuments] = useState<PurchaseReturnDocumentCandidate[]>([]);
  const [page, setPage] = useState(0);
  const [last, setLast] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const sequence = useRef(0);

  /** Al agregar más documentos a un evento que ya tiene uno seleccionado, la búsqueda se escopa a ese mismo proveedor. */
  const supplierId = supplierIdProp ?? selected[0]?.supplierId;

  const load = async (search: string, nextPage = 0) => {
    const request = ++sequence.current;
    setLoading(true);
    setError('');
    try {
      const response = await PurchaseReturnEventService.searchDocuments(search.trim(), nextPage, 10, {
        supplierId,
        startDate: isCompleteDate(startDate) ? startDate : undefined,
        endDate: isCompleteDate(endDate) ? endDate : undefined,
      });
      if (request !== sequence.current) return;
      setDocuments((current) => nextPage === 0 ? response.data.content : [...current, ...response.data.content]);
      setPage(response.data.number);
      setLast(response.data.last);
    } catch (caught: any) {
      if (request !== sequence.current) return;
      setError(caught?.response?.data?.message || 'No se pudieron consultar los documentos fiscales.');
      if (nextPage === 0) setDocuments([]);
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => void load(query, 0), 400);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, startDate, endDate, supplierId]);

  const selectedIds = useMemo(
    () => new Set([...selected.map((item) => item.purchaseId), ...selectedPurchaseIds]),
    [selected, selectedPurchaseIds]
  );
  const requiredDocumentType = documentTypeProp ?? selected[0]?.documentType;
  const disabledReason = (candidate: PurchaseReturnDocumentCandidate) => {
    if (!candidate.eligible) return candidate.blockingReasons.join(' ') || 'El backend indicó que este documento no es elegible.';
    if (selectedIds.has(candidate.purchaseId)) return 'Este documento ya fue agregado.';
    if (mode === 'multiple' && selected.length >= 50) return 'Puede seleccionar un máximo de 50 documentos.';
    if (mode === 'multiple' && requiredDocumentType && requiredDocumentType !== candidate.documentType) return 'Debe pertenecer al mismo tipo de DTE que los documentos seleccionados.';
    if (mode === 'multiple' && supplierId != null && supplierId !== candidate.supplierId) return 'Debe pertenecer al mismo proveedor que los documentos seleccionados.';
    return '';
  };

  const hasActiveFilters = !!query || !!startDate || !!endDate;
  const handleClearFilters = () => {
    setQuery('');
    setStartDate('');
    setEndDate('');
  };

  return (
    <section aria-label="Selector de documentos fiscales de compras" className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_auto] lg:items-end">
        <SearchInputField
          name="purchase-return-document-search"
          ariaLabel="Buscar documento fiscal de compra"
          label="Documento fiscal"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar por número de documento, código de generación, sello o proveedor"
          disabled={disabled}
          withMargin={false}
          rightContent={query ? <button type="button" aria-label="Limpiar búsqueda" onClick={() => setQuery('')} className="rounded-lg p-1 text-body hover:bg-gray-2 hover:text-primary dark:hover:bg-meta-4"><X className="h-4 w-4" /></button> : undefined}
        />
        <DatePicker label="Desde" name="purchase-return-document-start-date" value={startDate} disabled={disabled} onChange={setStartDate} withMargin={false} iconKey="calendar" />
        <DatePicker label="Hasta" name="purchase-return-document-end-date" value={endDate} disabled={disabled} onChange={setEndDate} withMargin={false} iconKey="calendar" />
        <ActionButton
          label="Borrar filtros"
          variant="secondary"
          size="sm"
          fullWidth={false}
          disabled={disabled || !hasActiveFilters}
          onClick={handleClearFilters}
        />
      </div>
      <div aria-live="polite" className="min-h-[2rem]">
        {loading && !documents.length && <div className="flex items-center gap-2 rounded-xl bg-primary/5 p-4 text-sm text-primary"><Loader2 className="h-4 w-4 animate-spin" /> Consultando documentos…</div>}
        {error && <div role="alert" className="rounded-xl border border-danger/30 bg-danger/10 p-4 text-sm text-danger">{error}</div>}
        {!loading && !error && !documents.length && <div className="rounded-xl border border-dashed border-stroke p-6 text-center text-sm text-body dark:border-strokedark"><FileSearch className="mx-auto mb-2 h-7 w-7 text-primary" />{query ? 'No se encontraron documentos con ese criterio.' : 'No hay documentos recientes disponibles.'}</div>}
      </div>
      {!!documents.length && <div className="divide-y divide-stroke overflow-hidden rounded-xl border border-stroke dark:divide-strokedark dark:border-strokedark">
        {documents.map((candidate) => {
          const reason = disabledReason(candidate);
          const isSelected = selectedIds.has(candidate.purchaseId);
          return <div
            key={candidate.purchaseId}
            title={reason || candidate.generationCode}
            className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm transition ${reason ? 'bg-gray-1 dark:bg-meta-4/40' : 'bg-white hover:bg-primary/5 dark:bg-boxdark'}`}
          >
            <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${candidate.eligible ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>{candidate.eligible ? 'Elegible' : 'No elegible'}</span>
            <div className="min-w-[140px] flex-1">
              <span className="truncate font-medium text-black dark:text-white">{candidate.documentNumber || candidate.controlNumber || 'Documento fiscal'}</span>
              <span className="ml-2 text-xs text-body">{documentTypeLabel(candidate.documentType)}</span>
              {reason && <p className="truncate text-xs text-danger">{reason}</p>}
            </div>
            <span className="w-36 shrink-0 truncate text-xs text-body" title={candidate.supplierName}>{candidate.supplierName || 'Sin proveedor'}</span>
            <span className="w-24 shrink-0 text-xs text-body">{dateLabel(candidate.purchaseDate)}</span>
            <span className="w-20 shrink-0 text-right text-xs font-medium text-black dark:text-white">{money(candidate.total)}</span>
            <span className="w-28 shrink-0 text-right text-xs text-body">Límite {dateLabel(candidate.deadline)}</span>
            <ActionButton
              label={isSelected ? 'Agregado' : 'Seleccionar'}
              icon={<CheckCircle2 className="h-4 w-4" />}
              disabled={disabled || !!reason}
              onClick={() => void onSelect(candidate)}
              size="sm"
              fullWidth={false}
              className="shrink-0"
            />
          </div>;
        })}
      </div>}
      {!last && <div className="flex justify-center"><ActionButton label="Cargar más" variant="secondary" size="sm" fullWidth={false} loading={loading} onClick={() => void load(query, page + 1)} /></div>}
    </section>
  );
}
